import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import {
  Close,
  Chat,
  Archive,
  HelpOutline,
  PictureAsPdf,
  InsertDriveFile,
  GetApp,
  AttachFile,
} from '@material-ui/icons';
import { IconButton, Tooltip, CircularProgress } from '@material-ui/core';
import { WechatOutlined } from '@ant-design/icons';
import {
  toast,
  resetUnreadCount,
  textSentByCurrentUser,
} from '../../../redux/action';
import {
  lastTextMessageConversationIdSelector,
  lastMessageIdSentByOrg,
  lastMessageIdSentByUser,
  lastStoredMediaMessage,
  userDetailSelector,
  selectedOrganizationSelector,
  textingHubViewers,
} from '../../../redux/selector';
import {
  getFullNameOrPhoneNumber,
  calculateSmsCharacterLength,
  calculateNumOfSmsTextMessages,
  calculateSmsMaxLength,
} from '../utils';
import {
  MEDIA_COMPRESSION_WARNING,
  OUTBOUND_MEDIA_ACCEPT,
} from '../utils/constants';
import { formatPhoneNumber } from '../../../Component/Analytics/utils';
import {
  sendTextMessage as sendTextMessageService,
  getMessageDataToSend,
  markMessageAsRead,
  fetchOrgTextMessages,
  getUnreadTextCount,
  fetchTextMessagesByConversationId,
  updateMessageStatus,
  uploadTextMedia,
  startConversation,
} from '../api';
import {
  Container,
  Header,
  Content,
  Title,
  ChatHistory,
  ReplyButton,
  SendIcon,
  LeftChatMessage,
  RightChatMessage,
  Date,
  LeftSender,
  RightSender,
  MetaData,
  TextArea,
  ReplyText,
  ReplyActionArea,
  ReplyActions,
  TextMetaData,
  ChatContainer,
  Line,
  TextHelp,
  SystemMessage,
  SystemSender,
  Italic,
  ErrorMsg,
  ReplyTextContainer,
  ViewedUsers,
  ViewedUserIcon,
  Bold,
  MediaContainer,
  MediaThumbnail,
  MediaFileChip,
  MediaFileName,
  MediaItemWrapper,
  MediaDownloadLink,
  AttachmentBar,
  AttachmentPreview,
  AttachmentThumbnail,
  AttachmentName,
  AttachmentMeta,
  AttachmentNotice,
} from './TextMessagesDrawer.styled';
import socketIOClient from 'socket.io-client';
import store from '../../../redux/store';
import EmptyConversation from './EmptyConversation';
import RepliedStopMessage from './RepliedStopMessage';
import DeliveryIssuesMessage from './DeliveryIssuesMessage';

const TextMessagesDrawer = ({
  onClose,
  message = {},
  conversationMessages = [],
  setTextMessages = () => {},
  isDrawerOpen,
  newContact, //for when a user initiates a text with a contact
  repliedStopPreviously, //if a contact replied stop previously
  hasDeliveryIssue, //if a contact had delivery issues previously
  setStatus,
  status,
}) => {
  const dispatch = useDispatch();
  const lastUpdatedMsgConversationId = useSelector(
    lastTextMessageConversationIdSelector
  );
  const lastMsgIdSentByOrg = useSelector(lastMessageIdSentByOrg);
  const viewers = useSelector(textingHubViewers);
  const lastMessageIdSentByUserSelector = useSelector(lastMessageIdSentByUser);
  const lastStoredMedia = useSelector(lastStoredMediaMessage);
  const user = useSelector(userDetailSelector);
  const organization = useSelector(selectedOrganizationSelector);
  const lastElem = useRef(null);
  const errorMsgRef = useRef(null);
  const contact = {
    ...message?.contact,
    contactPhone: message?.contactPhone,
  };
  const [text, setText] = useState('');
  const [currentConversation, setCurrentConversation] =
    useState(conversationMessages);
  const [maxCharLength, setMaxCharLength] = useState(1530);
  const [scrollToBottom, setScrollToBottom] = useState(false);
  const [numRows, setNumRows] = useState(3);
  const [errorMsg, setErrorMsg] = useState(null);
  const [messageStatus, setMessageStatus] = useState(
    message?.status || 'active'
  );
  // The attachment handle returned by the upload endpoint, or null. It is already
  // stored in S3 by the time it lands here — the composer only holds a reference.
  const [attachment, setAttachment] = useState(null);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [attachmentError, setAttachmentError] = useState(null);
  // Starting a conversation has no `message._id` to upload against, so the first
  // file pick finds or creates the conversation and keeps its id here. Kept out
  // of `textMessage.conversationId`, which would route the first send to the
  // reply endpoint and skip the STOP intro.
  const [pendingConversationId, setPendingConversationId] = useState(null);
  const fileInputRef = useRef(null);
  const mounted = useRef(false);

  const loadMessages = async conversationId => {
    const messages = await fetchTextMessagesByConversationId(conversationId);
    setCurrentConversation(messages);
    // this is a hack to make sure that scrolling to bottom is triggered once the data is rerendered
    setScrollToBottom(false);
  };

  const getDrawerData = () => {
    const conversationId =
      conversationMessages.length && conversationMessages[0]?.conversationId;
    const userId = userDetailSelector(store.getState()).id;
    const firstName = userDetailSelector(store.getState()).firstName || '';
    const lastName = userDetailSelector(store.getState()).lastName || '';
    const organizationId = selectedOrganizationSelector(store.getState()).id;
    return { conversationId, userId, organizationId, firstName, lastName };
  };

  useEffect(() => {
    const newSocket = socketIOClient(process.env.REACT_APP_andromedaAPI);
    newSocket.on('connect', () => {
      newSocket.emit('textDrawerOpened', getDrawerData());
    });

    mounted.current = true;
    return () => {
      mounted.current = false;
      newSocket.emit('textDrawerClosed', getDrawerData());
      newSocket.disconnect();
    };
  }, []);

  const getConversationViewers = () => {
    if (viewers && conversationMessages[0]?.conversationId in viewers) {
      const allViewers = viewers[conversationMessages[0]?.conversationId];
      return allViewers;
    }
    return [];
  };

  useEffect(() => {
    if (
      conversationMessages.length &&
      lastUpdatedMsgConversationId === conversationMessages[0].conversationId
    ) {
      loadMessages(lastUpdatedMsgConversationId);
    }
  }, [lastMessageIdSentByUserSelector]);

  useEffect(() => {
    if (
      conversationMessages.length &&
      lastMsgIdSentByOrg &&
      lastUpdatedMsgConversationId === conversationMessages[0].conversationId
    ) {
      loadMessages(lastUpdatedMsgConversationId);
    }
  }, [lastMsgIdSentByOrg]);

  // An inbound attachment arrives after its message: the first reload above
  // finds it still pending and hides it, so reload again once it is stored.
  useEffect(() => {
    if (
      conversationMessages.length &&
      lastStoredMedia?.conversationId === conversationMessages[0].conversationId
    ) {
      loadMessages(lastStoredMedia.conversationId);
    }
  }, [lastStoredMedia]);

  const scrollToLastMessage = () => {
    lastElem?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const formatTime = date => {
    return moment(date).format('MMMM D, h:mm a');
  };

  const markUnreadMessagesIfAny = async lastMessage => {
    if (lastMessage.status === 'unread') {
      try {
        await markMessageAsRead(lastMessage.conversationId);
        const unreadCount = await getUnreadTextCount();
        dispatch(resetUnreadCount(unreadCount));
        const updatedMessage = { ...lastMessage, isRead: true, contact };
        setTextMessages(messages => [
          updatedMessage,
          ...messages.filter(
            msg => msg.conversationId !== updatedMessage.conversationId
          ),
        ]);

        setCurrentConversation(messages =>
          messages.map(msg => {
            msg.status = 'read';
            return msg;
          })
        );
      } catch (e) {
        console.log(e);
        dispatch(toast('error', 'Failed to mark latest messages as read'));
      }
    }
  };

  const openMediaOriginal = url => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  // Offered on an existing conversation, and when starting one with a contact
  // who has not replied STOP and has no delivery issue (those screens have no
  // reply area at all).
  const canAttachMedia =
    Boolean(message?._id) ||
    Boolean(newContact && !repliedStopPreviously && !hasDeliveryIssue);

  const clearAttachment = () => {
    setAttachment(null);
    setAttachmentError(null);
    // Reset the input's value so re-picking the same file still fires onChange.
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // A conversation id and an upload belong to one contact. If the contact being
  // started with changes, neither may carry over to the next one.
  useEffect(() => {
    setPendingConversationId(null);
    clearAttachment();
  }, [newContact?.contactPhone]);

  // Uploads as soon as a file is chosen, so the send itself only has to reference
  // an object that already exists. Picking a second file replaces the first —
  // one attachment per message.
  const handleFileSelected = async event => {
    const file = event.target.files?.[0];
    if (!file) return;

    setAttachment(null);
    setAttachmentError(null);
    setUploadingAttachment(true);
    try {
      let conversationId = message?._id || pendingConversationId;
      if (!conversationId) {
        conversationId = await startConversation({
          contactPhone: newContact.contactPhone,
          noteRouterId: newContact.value,
        });
        setPendingConversationId(conversationId);
      }
      const uploaded = await uploadTextMedia({
        file,
        organizationId: organization.id,
        conversationId,
      });
      setAttachment(uploaded);
    } catch (error) {
      // The endpoint writes a message meant to be shown as-is for anything the
      // user could fix by picking a different file.
      setAttachmentError(
        error?.response?.data?.message ||
          'Sorry, that attachment could not be uploaded.'
      );
    } finally {
      setUploadingAttachment(false);
      // Always clear the input, so the same file can be retried after a failure.
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const formatAttachmentSize = bytes =>
    typeof bytes === 'number' && bytes > 0
      ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
      : '';

  // Only stored media renders. The API already returns nothing else — media we
  // could not store is dropped there, and so is media whose ingest has not
  // finished, which arrives on a later fetch once it is stored. This repeats the
  // check so an older message read from anywhere else cannot slip an item
  // through that has no URLs on it.
  const getRenderableMedia = media =>
    Array.isArray(media) ? media.filter(item => item.status === 'stored') : [];

  // Renders a single media item as its own message body. Images use the thumbnail
  // and open the full original on click. Identical for both directions apart from
  // which side of the thread it sits on.
  const renderMediaItem = (item, key, { outbound = false } = {}) => {
    const isPdf = item.contentType === 'application/pdf';
    // Only outbound media carries a name — a staff member picked the file, so
    // the chip says what they sent instead of a generic label. Inbound media has
    // none (Twilio sends no filename) and keeps the content-type-derived label.
    const fallbackLabel = isPdf ? 'PDF' : 'View file';
    const preview =
      isPdf || !item.thumbnailUrl ? (
        // PDFs (and any stored file without a thumbnail) render as a
        // clickable chip that opens the original in a new tab — the
        // browser's native viewer handles the PDF.
        <MediaFileChip
          onClick={() => openMediaOriginal(item.url)}
          title={item.fileName || undefined}
        >
          {isPdf ? <PictureAsPdf /> : <InsertDriveFile />}
          <MediaFileName>{item.fileName || fallbackLabel}</MediaFileName>
        </MediaFileChip>
      ) : (
        <MediaThumbnail
          src={item.thumbnailUrl}
          alt={item.fileName || 'attachment'}
          title={item.fileName || undefined}
          onClick={() => openMediaOriginal(item.url)}
          // The image has no height until it loads, so the scroll to the
          // bottom that ran on render stops short of it. Scroll again now.
          onLoad={scrollToLastMessage}
        />
      );
    return (
      <MediaContainer outbound={outbound}>
        <MediaItemWrapper key={key}>
          {preview}
          {item.downloadUrl ? (
            <MediaDownloadLink href={item.downloadUrl}>
              <GetApp /> Download
            </MediaDownloadLink>
          ) : null}
        </MediaItemWrapper>
      </MediaContainer>
    );
  };

  const renderChatHistory = conversationMessages => {
    const chatHistory = [];

    for (let i = 0; i < conversationMessages.length; i++) {
      if (conversationMessages[i].senderType === 'contact') {
        const message = conversationMessages[i];
        const renderableMedia = getRenderableMedia(message.media);
        const metaData = (
          <MetaData>
            <LeftSender>
              {getFullNameOrPhoneNumber({ ...message, ...contact })}
            </LeftSender>
            <Date>{formatTime(message.createdAt)}</Date>
          </MetaData>
        );

        // One MMS can carry several attachments. Each one renders as its own
        // message rather than sharing a single row, so the body text and every
        // attachment get their own bubble.
        if (message.body) {
          chatHistory.push(
            <ChatContainer key={`${message._id}-text`}>
              {metaData}
              <LeftChatMessage>{message.body}</LeftChatMessage>
            </ChatContainer>
          );
        }
        renderableMedia.forEach((item, index) => {
          const key = `${message._id}-media-${index}`;
          chatHistory.push(
            <ChatContainer key={key}>
              {metaData}
              {renderMediaItem(item, key)}
            </ChatContainer>
          );
        });
      } else if (conversationMessages[i].keywordAutoreply) {
        chatHistory.push(
          <ChatContainer key={conversationMessages[i]._id}>
            <MetaData>
              <Date>{formatTime(conversationMessages[i].createdAt)}</Date>
              <SystemSender>NoteRouter System</SystemSender>
            </MetaData>
            <SystemMessage>
              {conversationMessages[i].systemMessage}
              <Italic>{conversationMessages[i].keywordAutoreply}</Italic>
            </SystemMessage>
          </ChatContainer>
        );
      } else if (conversationMessages[i].systemMessage) {
        chatHistory.push(
          <ChatContainer key={conversationMessages[i]._id}>
            <MetaData>
              <Date>{formatTime(conversationMessages[i].createdAt)}</Date>
              <SystemSender>NoteRouter System</SystemSender>
            </MetaData>
            <SystemMessage>
              {conversationMessages[i].systemMessage}
            </SystemMessage>
          </ChatContainer>
        );
      } else {
        const message = conversationMessages[i];
        const renderableMedia = getRenderableMedia(message.media);
        const metaData = (
          <MetaData>
            <Date>{formatTime(message.createdAt)}</Date>
            <RightSender>
              {getFullNameOrPhoneNumber({ ...message, ...contact })}
            </RightSender>
          </MetaData>
        );

        // Same shape as the inbound branch: the body gets its own bubble, then
        // each attachment gets its own, so a reply with both reads as two entries
        // rather than one crowded row. A media-only reply has no body at all.
        if (message.body) {
          chatHistory.push(
            <ChatContainer key={`${message._id}-text`}>
              {metaData}
              <RightChatMessage>{message.body}</RightChatMessage>
            </ChatContainer>
          );
        }
        renderableMedia.forEach((item, index) => {
          const key = `${message._id}-media-${index}`;
          chatHistory.push(
            <ChatContainer key={key}>
              {metaData}
              {renderMediaItem(item, key, { outbound: true })}
            </ChatContainer>
          );
        });
      }
    }
    chatHistory.push(<span key="lastElem" ref={lastElem} />);
    setTimeout(() => {
      setScrollToBottom(true);
    }, 1000);
    markUnreadMessagesIfAny(
      conversationMessages[conversationMessages.length - 1]
    );
    return chatHistory;
  };

  useEffect(() => {
    scrollToLastMessage();
    if (errorMsg) {
      errorMsgRef?.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [scrollToBottom]);

  const handleTextChange = event => {
    const text = event.target.value;
    const maxLength = calculateSmsMaxLength(text);
    setMaxCharLength(maxLength);

    if (calculateSmsCharacterLength(text) > maxLength) {
      let endChar = maxLength;
      // in case of copy paste for example, we allow only to paste just enough text to reach the max limit
      while (
        calculateSmsCharacterLength(text.substring(0, endChar)) > maxLength
      ) {
        endChar--;
      }
      setText(text.substring(0, endChar));
    } else {
      setText(text || '');
    }
  };

  const isMaxTextLengthReached = useMemo(() => {
    return calculateSmsCharacterLength(text) >= maxCharLength;
  }, [text, maxCharLength]);

  const textareaStyle = useMemo(() => {
    return isMaxTextLengthReached
      ? { borderColor: 'red', outline: 'none' }
      : {};
  }, [isMaxTextLengthReached]);

  const sendTextMessage = async () => {
    // An attachment on its own is a valid reply, so text is only required when
    // there is nothing else to send. An upload still in flight blocks the send —
    // the message must not reference an object that is not stored yet.
    if (!text?.trim() && !attachment) return;
    if (uploadingAttachment) return;
    try {
      const { textMessage, user } = getMessageDataToSend({
        contactPhone: contact.contactPhone || newContact.contactPhone,
        body: text,
        conversationId: message?._id,
        // Only the fields the backend persists. `thumbnailUrl` is composer-only
        // and deliberately left behind: a signed URL must never be persisted.
        // `fileName` is the name the upload endpoint derived, echoed back so the
        // chip in the thread and the download carry it too.
        ...(attachment && {
          media: [
            {
              s3Key: attachment.s3Key,
              thumbS3Key: attachment.thumbS3Key,
              contentType: attachment.contentType,
              sizeBytes: attachment.sizeBytes,
              width: attachment.width,
              height: attachment.height,
              fileName: attachment.fileName,
            },
          ],
        }),
      });

      setErrorMsg(null);

      // for user initiated text
      let initialText;

      if (!textMessage.conversationId) {
        initialText = `${user.firstName} from ${organization.acronym} is sending you a text message. You can reply STOP anytime to stop messages from this number.`;

        const initialTextMessage = getMessageDataToSend({
          contactPhone: newContact.contactPhone,
          body: initialText,
        });

        setCurrentConversation([
          // _id is just a temp value. I implemented it this way so that a person can see their sent message instantly
          {
            ...initialTextMessage.textMessage,
            user: initialTextMessage.user,
            _id: uuidv4(),
          },
          {
            ...textMessage,
            // Only the second message carries the attachment — the STOP intro is
            // always text only. Same optimistic shape as a reply, below.
            ...(attachment && {
              media: [{ ...attachment, status: 'stored' }],
            }),
            user,
            _id: uuidv4(),
          },
        ]);
      } else {
        setCurrentConversation(currentConversation => [
          ...currentConversation,
          // _id is just a temp value. I implemented it this way so that a person can see their sent message instantly
          {
            ...textMessage,
            // The optimistic copy needs `status: 'stored'` to get past
            // getRenderableMedia, and the composer's `thumbnailUrl` to draw
            // anything. It has no `url` yet, so the bubble is not clickable until
            // the reload below replaces it with the persisted message.
            ...(attachment && {
              media: [{ ...attachment, status: 'stored' }],
            }),
            user,
            _id: uuidv4(),
          },
        ]);
      }

      const sentAttachment = attachment;
      setText('');
      clearAttachment();
      // this is a hack to make sure that scrolling to bottom is triggered once the data is rerendered
      setScrollToBottom(false);
      const newConversationId = await sendTextMessageService({
        textMessage,
        initialText,
        noteRouterId: newContact?.value,
        conversationId: pendingConversationId,
      });
      dispatch(textSentByCurrentUser());

      // A later attach from this screen uploads under the same conversation.
      if (!textMessage.conversationId && newConversationId) {
        setPendingConversationId(newConversationId);
      }

      // if this is the beginning of a message thread, make sure to load first messages from database including conversationId
      if (!textMessage.conversationId) {
        loadMessages(newConversationId);
      } else if (sentAttachment) {
        // Reload so the optimistic entry is replaced by the persisted message,
        // which carries the read-time signed `url`/`downloadUrl` the bubble needs
        // to be clickable. Only done when media was sent — a text-only reply has
        // nothing extra to fetch.
        loadMessages(textMessage.conversationId);
      }

      const [updatedMessage] = await fetchOrgTextMessages({
        conversationId: `${textMessage.conversationId || newConversationId}`,
        status: message?.status,
      });
      setTextMessages(messages => [
        { ...updatedMessage, status: 'active' },
        ...messages.filter(msg => msg._id !== updatedMessage._id),
      ]);
      if (message?.status === 'archived') {
        await updateMessageStatus([textMessage.conversationId], 'active');
        if (status === 'archived') {
          setTextMessages(messages => [
            ...messages.filter(msg => msg._id !== updatedMessage._id),
          ]);
          onClose();
          setStatus('active');
        }
        // Update local message status to reflect the change immediately
        setMessageStatus('active');
        dispatch(toast('success', 'Successfully unarchived message thread'));
      }
    } catch (error) {
      console.log('error', error);
      setScrollToBottom(false);
      setErrorMsg('Sorry, there was an error sending this reply.');
      setScrollToBottom(true);
    }
  };

  useEffect(() => {
    const savedText = localStorage.getItem('savedText');
    if (savedText?.trim()) {
      setText(savedText);
    }
  }, []);

  useEffect(() => {
    // every time the drawer is closed, trigger saving current text
    if (!isDrawerOpen) {
      localStorage.setItem('savedText', text.trim());
    }
  }, [isDrawerOpen]);

  useEffect(() => {
    const calculateNumRows = () => {
      const drawerWidth = window.innerWidth * 0.4;
      // the formula below is calculated through trial and error
      const calculatedNumRows = Math.floor(text.length / (drawerWidth / 10));

      const numRows =
        calculatedNumRows < 4
          ? 3
          : calculatedNumRows > 10
          ? 10
          : calculatedNumRows;
      setNumRows(numRows);
    };

    calculateNumRows();

    const handleResize = () => {
      calculateNumRows();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [text]);

  const handleKeyPress = event => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      sendTextMessage();
    }
  };

  const getName = () => {
    if (!message?.contact || !message?.contactPhone)
      return 'New Text Conversation';

    const contact = message?.contact;
    let contactDetails = '';
    if (contact?.firstName || contact?.firstName) {
      contactDetails = `${contact.firstName || ''} ${contact.lastName || ''}`;
    } else {
      contactDetails = formatPhoneNumber(message?.contactPhone);
    }
    return `Text conversation with ${contactDetails}`;
  };

  const getContent = () => {
    if (repliedStopPreviously) {
      return <RepliedStopMessage newContact={newContact} />;
    } else if (hasDeliveryIssue) {
      return <DeliveryIssuesMessage newContact={newContact} />;
    } else if (newContact && !currentConversation.length) {
      // if there is a newContact and there is no repliedStopPreviously or hasDeliveryIssue
      return (
        <EmptyConversation
          newContact={newContact}
          user={user}
          organization={organization}
        />
      );
    } else {
      return renderChatHistory(currentConversation);
    }
  };

  const getReplyArea = () => {
    if (repliedStopPreviously || hasDeliveryIssue) return '';

    return (
      <>
        <ReplyTextContainer>
          <ReplyText>
            <Chat fontSize="small" />
            Reply
          </ReplyText>
          {getConversationViewers().length > 0 && (
            <ViewedUsers>
              <ViewedUserIcon />
              {'Currently being viewed by '}
              {getConversationViewers()[0]?.fullName}{' '}
              {getConversationViewers().length > 1
                ? ' and ' + getConversationViewers().length - 1 + ' user'
                : ''}
            </ViewedUsers>
          )}
        </ReplyTextContainer>
        {message?.status === 'spammed' ? (
          <Bold>
            In order to send a reply, you must unspam the conversation first.
          </Bold>
        ) : (
          <>
            {canAttachMedia && (attachment || attachmentError) ? (
              <AttachmentBar>
                {attachment ? (
                  <AttachmentPreview>
                    {attachment.thumbnailUrl ? (
                      <AttachmentThumbnail
                        src={attachment.thumbnailUrl}
                        alt="attachment preview"
                      />
                    ) : (
                      <PictureAsPdf style={{ color: '#d0021b' }} />
                    )}
                    <AttachmentName title={attachment.fileName}>
                      {attachment.fileName || 'Attachment'}
                    </AttachmentName>
                    <AttachmentMeta>
                      {formatAttachmentSize(attachment.sizeBytes)}
                    </AttachmentMeta>
                    <Tooltip title="Remove attachment">
                      <IconButton size="small" onClick={clearAttachment}>
                        <Close style={{ fontSize: '16px' }} />
                      </IconButton>
                    </Tooltip>
                  </AttachmentPreview>
                ) : null}
                {attachment?.degraded ? (
                  <AttachmentNotice>
                    {MEDIA_COMPRESSION_WARNING}
                  </AttachmentNotice>
                ) : null}
                {attachmentError ? (
                  <AttachmentNotice error>{attachmentError}</AttachmentNotice>
                ) : null}
              </AttachmentBar>
            ) : null}
            <TextArea
              placeholder="Type your reply here"
              rows={numRows}
              value={text}
              onChange={handleTextChange}
              maxLength={maxCharLength}
              style={textareaStyle}
              onKeyDown={handleKeyPress}
            />
            <ReplyActionArea>
              <TextMetaData>
                <span>
                  Characters: {calculateSmsCharacterLength(text)}/
                  {maxCharLength}
                </span>
                <TextHelp>
                  Texts: {calculateNumOfSmsTextMessages(text)}{' '}
                  <Tooltip title="A single SMS text segment can fit 153 to 160 characters. However, special characters like emojis reduce the maximum size of a text segment to 67 characters.">
                    <HelpOutline style={{ fontSize: '14px' }} />
                  </Tooltip>
                </TextHelp>
              </TextMetaData>
              <ReplyActions>
                {canAttachMedia ? (
                  <>
                    {/* Hidden input driven by the icon button. Chosen over
                        react-file-reader (used for CSV uploads elsewhere) for direct
                        control over `accept` and single-file selection. */}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept={OUTBOUND_MEDIA_ACCEPT}
                      onChange={handleFileSelected}
                      style={{ display: 'none' }}
                    />
                    <Tooltip
                      title={
                        attachment
                          ? 'Replace attachment'
                          : 'Attach an image or PDF'
                      }
                    >
                      <IconButton
                        size="small"
                        disabled={uploadingAttachment}
                        onClick={() => fileInputRef.current?.click()}
                      >
                        {uploadingAttachment ? (
                          <CircularProgress size={18} />
                        ) : (
                          <AttachFile fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  </>
                ) : null}
                <ReplyButton
                  onClick={sendTextMessage}
                  disabled={(!text && !attachment) || uploadingAttachment}
                >
                  <SendIcon fontSize={'small'} /> Reply
                </ReplyButton>
              </ReplyActions>
            </ReplyActionArea>
          </>
        )}
      </>
    );
  };

  const getTitle = () => {
    if (repliedStopPreviously || hasDeliveryIssue)
      return `Unable to start converstaion with  ${newContact.firstName} ${newContact.lastName}`;
    return 'Conversation';
  };

  return (
    <Container>
      <Header>
        <span>
          <IconButton onClick={onClose}>
            <Close style={{ color: 'white' }} />
          </IconButton>
          {getName()}
        </span>
        {messageStatus === 'archived' ? (
          <Line>
            Archived
            <Archive style={{ fontSize: '18px' }} />
          </Line>
        ) : (
          ''
        )}
      </Header>
      <Content>
        <Title>
          <WechatOutlined style={{ color: '#007ADE', fontSize: '24px' }} />
          {getTitle()}
        </Title>
        <ChatHistory>
          {getContent()}
          {errorMsg && <ErrorMsg ref={errorMsgRef}>{errorMsg}</ErrorMsg>}
        </ChatHistory>
        {getReplyArea()}
      </Content>
    </Container>
  );
};

export default TextMessagesDrawer;
